/**
 * Marine vessel type → solid bow-up silhouette resolver (L.2.2, R.1d render).
 *
 * The marine sibling of aircraftSilhouettes.ts. The tracked_vessels roster carries
 * a `vessel_type` from the R.1d taxonomy (icebreaker | hero_patrol | cape_lifeboat
 * | bay_lifeboat | catamaran_patrol | rhib | hovercraft | science | navaids |
 * offshore_patrol). Each maps to a distinct bow-up silhouette so a vessel reads as
 * "what kind of hull" by SHAPE in the ambient register (§3B.8 — SHAPE encodes kind,
 * COLOR is ambient/desaturated, vessels NEVER pulse).
 *
 * Silhouettes are bow-up (north) so a Cesium billboard `rotation` of the negated
 * compass heading points the hull along its track (same contract as aircraft).
 * Authored inline as `currentColor` SVG strings (tinted via svgIcon.tintedSvgDataUrl)
 * — no per-file asset wiring needed for this small fixed set.
 */
import { tintedSvgDataUrl } from './svgIcon';

export type VesselSilhouetteKey =
  | 'icebreaker'
  | 'hero_patrol'
  | 'offshore_patrol'
  | 'cape_lifeboat'
  | 'bay_lifeboat'
  | 'catamaran_patrol'
  | 'rhib'
  | 'hovercraft'
  | 'science'
  | 'navaids'
  | 'generic';

/** viewBox 0 0 24 24, bow toward +Y (north/up), fill="currentColor". */
export const VESSEL_SILHOUETTE: Record<VesselSilhouetteKey, string> = {
  // Squat, wide, blunt-bow hull — heavy icebreaker.
  icebreaker:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M6 5h12v8c0 3-2.5 6-6 6s-6-3-6-6V5z"/><rect fill="currentColor" x="9.5" y="2.5" width="5" height="4" rx="1"/></svg>',
  // Pointed-bow patrol cutter, medium.
  hero_patrol:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2l5 6v8c0 2-2.2 4-5 4s-5-2-5-4V8l5-6z"/></svg>',
  // Larger, longer cutter — offshore patrol.
  offshore_patrol:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 1l5 7v9c0 2.2-2.2 4-5 4s-5-1.8-5-4V8l5-7z"/><rect fill="currentColor" x="10.5" y="9" width="3" height="6" rx="0.5" opacity="0.6"/></svg>',
  // SAR diamond — Cape-class lifeboat (rescue read).
  cape_lifeboat:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2l7 10-7 10-7-10z"/></svg>',
  // Smaller SAR diamond — Bay-class lifeboat.
  bay_lifeboat:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 5l5 7-5 7-5-7z"/></svg>',
  // Twin-hull catamaran (two parallel pointed hulls).
  catamaran_patrol:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M8 3l2 5v11c0 1-2 1-2 0V8L6 3z" transform="translate(1 0)"/><path fill="currentColor" d="M16 3l2 5v11c0 1-2 1-2 0V8l-2-5z" transform="translate(-1 0)"/></svg>',
  // Small rounded inflatable — RHIB.
  rhib: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 6c3 0 5 2 5 6s-2 6-5 6-5-2-5-6 2-6 5-6z"/></svg>',
  // Oval — hovercraft.
  hovercraft:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><ellipse fill="currentColor" cx="12" cy="12" rx="6" ry="8.5"/></svg>',
  // Ship + antenna mast — science vessel.
  science:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M7 7h10v7c0 2.5-2.2 5-5 5s-5-2.5-5-5V7z"/><rect fill="currentColor" x="11.3" y="1" width="1.4" height="6"/><circle fill="currentColor" cx="12" cy="1.5" r="1.6"/></svg>',
  // Ship + beacon light — navaids tender.
  navaids:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M7 7h10v7c0 2.5-2.2 5-5 5s-5-2.5-5-5V7z"/><circle fill="currentColor" cx="12" cy="3" r="2.4"/></svg>',
  // Plain pointed hull fallback.
  generic:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 3l5 6v9c0 1.8-2.2 3-5 3s-5-1.2-5-3V9l5-6z"/></svg>',
};

/** Human label per silhouette, for the side panel / a future legend. */
export const VESSEL_LABEL: Record<VesselSilhouetteKey, string> = {
  icebreaker: 'Icebreaker',
  hero_patrol: 'Hero-class patrol vessel',
  offshore_patrol: 'Offshore patrol vessel',
  cape_lifeboat: 'Cape-class SAR lifeboat',
  bay_lifeboat: 'Bay-class SAR lifeboat',
  catamaran_patrol: 'Catamaran patrol vessel',
  rhib: 'Rigid-hull inflatable (RHIB)',
  hovercraft: 'Hovercraft',
  science: 'Science / survey vessel',
  navaids: 'Navaids tender',
  generic: 'Vessel',
};

/** Map a tracked_vessels.vessel_type to a silhouette key (degrades to generic). */
export function resolveVesselSilhouette(vesselType: string | null): VesselSilhouetteKey {
  const t = (vesselType ?? '').trim().toLowerCase();
  if (t in VESSEL_SILHOUETTE && t !== 'generic') return t as VesselSilhouetteKey;
  return 'generic';
}

/** Resolve + tint a vessel silhouette to a cached data-URL (heading applied by the
 *  caller via Cesium billboard rotation — one texture per type+colour). */
export function vesselSilhouetteUrl(vesselType: string | null, color: string): string {
  const key = resolveVesselSilhouette(vesselType);
  return tintedSvgDataUrl(`vessel:${key}`, VESSEL_SILHOUETTE[key], color);
}
